import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { ExpandMore, Info, Warning, CheckCircle } from '@mui/icons-material';

export default function UpdateGuidePanel() {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">Sistema di Aggiornamento Automatico per Kiosk</Typography>
        Questa guida spiega come gestire gli aggiornamenti APK dei dispositivi kiosk distribuiti.
      </Alert>

      {/* Stati delle Versioni */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">📊 Stati delle Versioni</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Box>
              <Chip label="ACTIVE" color="success" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2">
                <strong>Versione Attiva:</strong> La versione che viene attualmente distribuita ai dispositivi.
                Solo UNA versione può essere ACTIVE alla volta.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • <strong>Modificabile solo se nessun dispositivo la sta usando</strong><br />
                • Può essere disattivata (diventa INACTIVE)<br />
                • Può essere sostituita attivando un'altra versione<br />
                • Non può essere eliminata se ci sono dispositivi che la usano
              </Typography>
            </Box>

            <Box>
              <Chip label="INACTIVE" color="default" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2">
                <strong>Versione Inattiva:</strong> Versione pronta ma non distribuita.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Può essere modificata<br />
                • Può essere attivata<br />
                • Può essere archiviata o eliminata
              </Typography>
            </Box>

            <Box>
              <Chip label="ARCHIVED" color="default" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2">
                <strong>Versione Archiviata:</strong> Versione storica non più distribuibile.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Visibile solo per riferimento storico<br />
                • Non può essere attivata<br />
                • Può essere eliminata
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Rollout Percentuale */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">📈 Rollout Percentuale (Distribuzione Graduale)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Typography variant="body2">
              Il rollout percentuale <strong>non</strong> significa che ogni dispositivo ha una probabilità casuale 
              ad ogni controllo. È un sistema <strong>deterministico</strong> basato su hash SHA-256 dell'UUID del dispositivo.
            </Typography>

            <Alert severity="info" icon={<Info />}>
              <Typography variant="subtitle2">Come Funziona (Sistema Deterministico)</Typography>
              <Typography variant="body2" component="div">
                1. Ogni dispositivo calcola un hash del suo UUID → valore fisso tra 0-99<br />
                2. Con rollout 50%, solo dispositivi con hash 0-49 ricevono l'update<br />
                3. <strong>Lo stesso dispositivo riceve SEMPRE lo stesso risultato</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                Se un dispositivo rientra nel 50%, rientrerà ad ogni controllo.<br />
                Se NON rientra nel 50%, NON rientrerà mai fino a cambio di rollout.
              </Typography>
            </Alert>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2">Esempio Concreto (Rollout 50%):</Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
              <Typography variant="body2" component="div">
                • Device A (UUID: abc-123) → hash = 25 → <strong>✅ RIENTRA</strong> (25 &lt; 50)<br />
                • Device B (UUID: def-456) → hash = 75 → <strong>❌ NON RIENTRA</strong> (75 ≥ 50)<br />
                <br />
                <strong>Risultato ad ogni controllo:</strong><br />
                - Device A riceverà SEMPRE l'aggiornamento (hash fisso = 25)<br />
                - Device B NON riceverà MAI l'aggiornamento (hash fisso = 75)<br />
                - Cambiando rollout a 80%, anche Device B inizierebbe a riceverlo
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2">Percentuali Tipiche:</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Rollout 10%:</strong> Solo il 10% dei dispositivi (sempre gli stessi)<br />
                <Typography variant="caption" color="text.secondary">
                  Ideale per beta testing con device specifici
                </Typography>
              </li>
              <li>
                <strong>Rollout 50%:</strong> Metà dei dispositivi (sempre gli stessi)<br />
                <Typography variant="caption" color="text.secondary">
                  Distribuzione graduale, statisticamente metà del fleet
                </Typography>
              </li>
              <li>
                <strong>Rollout 100%:</strong> Tutti i dispositivi<br />
                <Typography variant="caption" color="text.secondary">
                  Distribuzione completa, nessuna limitazione
                </Typography>
              </li>
            </Box>

            <Alert severity="warning" icon={<Warning />}>
              <Typography variant="body2">
                <strong>Attenzione con pochi dispositivi:</strong> Con 2-10 dispositivi, la distribuzione statistica 
                potrebbe non essere esatta. Ad esempio, con 2 dispositivi e rollout 50%, potrebbero rientrare 
                entrambi o nessuno (25% di probabilità ciascuno), perché dipende dai loro hash casuali.
                Il sistema è ottimizzato per flotte di centinaia/migliaia di dispositivi.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Auto-Check vs Force Check */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">🔄 Auto-Check vs Force Check</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <CheckCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Auto-Check (Bootstrap + Periodic Check)
              </Typography>
              <Typography variant="body2">
                Ogni dispositivo esegue un controllo automatico sia <strong>all'avvio del bootstrap</strong> sia poi a intervalli periodici configurabili.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Rispetta la percentuale di rollout<br />
                • Rispetta la maintenance window (se abilitata)<br />
                • Il bootstrap check usa il flusso normale, senza bypass speciali<br />
                • Riceve solo upgrade e rollback automatici<br />
                • Non richiede intervento manuale
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Bootstrap check: comportamento esplicito
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Rollout:</strong> se il dispositivo non rientra nella percentuale configurata, il check di bootstrap <strong>non</strong> riceve alcun update e l'avvio continua normalmente.<br />
                  • <strong>Maintenance window:</strong> se il dispositivo rientra nel rollout ma l'installazione non è consentita in quel momento, il bootstrap <strong>vede</strong> l'update ma <strong>non lo installa</strong>; l'app continua ad avviarsi e l'installazione resta posticipata alla prima finestra utile.<br />
                  • In altre parole: il bootstrap check <strong>non forza nulla</strong>, applica le stesse regole normali del backend.
                </Typography>
              </Alert>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <Warning fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Force Check
              </Typography>
              <Typography variant="body2">
                Check manuale forzato dall'Admin Screen del kiosk o dall'Admin Webapp.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • <strong>BYPASSA</strong> il rollout (100% sempre)<br />
                • <strong>BYPASSA</strong> la maintenance window<br />
                • Utile per debugging o aggiornamenti urgenti<br />
                • Richiede azione esplicita dell'amministratore
              </Typography>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Caso d'uso:</strong> Se un dispositivo è al di fuori del rollout 50%, 
                né il bootstrap check né il periodic check gli offriranno l'update. Usa Force Check per forzarlo.
              </Typography>
            </Alert>

            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                Differenza importante
              </Typography>
              <Typography variant="body2" component="div">
                • <strong>Update non disponibile:</strong> il device è fuori rollout oppure non esiste una versione candidata.<br />
                • <strong>Update disponibile ma non installabile ora:</strong> il device rientra nel rollout, ma la maintenance window impedisce temporaneamente l'installazione.
              </Typography>
            </Alert>

            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Cosa succede fuori maintenance window
              </Typography>
              <Typography variant="body2">
                Il device può rilevare la nuova versione durante bootstrap o periodic check, ma l'app continua normalmente e l'installazione viene rinviata alla prima finestra valida, salvo force check.
              </Typography>
            </Alert>

            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Perché esiste il Force Check
              </Typography>
              <Typography variant="body2">
                Serve per test immediati, debugging, verifiche su singoli device e interventi urgenti, senza dover aspettare che il device rientri nel rollout o nella maintenance window.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Maintenance Window */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">🕐 Maintenance Window (Finestra di Manutenzione)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Typography variant="body2">
              La maintenance window limita quando i dispositivi possono installare aggiornamenti automaticamente.
              Previene installazioni durante orari di punta.
            </Typography>

            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, my: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Esempio Configurazione:</Typography>
              <Typography variant="body2" component="div">
                • <strong>Abilitata:</strong> Sì<br />
                • <strong>Orario (UTC):</strong> 02:00 - 06:00<br />
                • <strong>Effetto:</strong> Aggiornamenti solo tra le 2 e le 6 di notte (UTC)
              </Typography>
            </Box>

            <Typography variant="subtitle2" gutterBottom>Comportamento:</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Durante la finestra:</strong> Aggiornamenti installati normalmente
              </li>
              <li>
                <strong>Fuori dalla finestra:</strong> Update rilevato ma installazione posticipata
              </li>
              <li>
                <strong>Force Check:</strong> BYPASSA la maintenance window (sempre installabile)
              </li>
            </Box>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Attenzione:</strong> Usa sempre orario UTC! Considera i fusi orari dei tuoi dispositivi.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Rollback Funzionale */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">⏮️ Rollback Funzionale (Strategia Sicura)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Alert severity="error" icon={<Warning />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">🚫 Rollback con Downgrade versionCode NON FUNZIONA</Typography>
              <Typography variant="body2">
                <strong>Android blocca il downgrade per policy di sicurezza.</strong><br />
                Tentare di installare versionCode 10216 su dispositivi con 10217 = INSTALL_FAILED_VERSION_DOWNGRADE
              </Typography>
            </Alert>

            <Typography variant="body2">
              <strong>Regola d'oro Android:</strong> versionCode deve essere sempre crescente (monotono).
              Per rollback "sicuro", usa l'approccio <strong>Rollback Funzionale</strong>.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>🎯 Strategia Rollback Funzionale:</Typography>
            <Box sx={{ bgcolor: '#E3F2FD', p: 2, borderRadius: 1, mb: 2, border: '1px solid #90CAF9' }}>
              <Typography variant="body2" component="div">
                <strong>Scenario:</strong> Bug critico in v1.2.17 (versionCode 10217), vuoi tornare a v1.2.16<br /><br />
                <strong>❌ SBAGLIATO (downgrade):</strong><br />
                Pubblicare v1.2.16 (versionCode 10216) → Android blocca installazione<br /><br />
                <strong>✅ CORRETTO (rollback funzionale):</strong><br />
                1. Recupera il CODICE di v1.2.16 (git checkout, backup, etc.)<br />
                2. Bumpa version a v1.2.18 (versionCode <strong>10218</strong>)<br />
                3. Opzionale: rinomina "v1.2.18-rollback-to-1.2.16" nelle release notes<br />
                4. Build APK e carica come nuova versione<br />
                5. Pubblica come ACTIVE<br />
                6. I dispositivi vedono upgrade 10217 → <strong>10218</strong> ✅ (funziona!)<br />
                7. Funzionalmente tornano a v1.2.16, ma Android vede upgrade
              </Typography>
            </Box>

            <Typography variant="subtitle2" gutterBottom>📋 Workflow Dettagliato:</Typography>
            <Box component="ol" sx={{ pl: 2 }}>
              <li>
                <strong>Identifica versione target:</strong> es. v1.2.16 (commit sha: abc1234)
              </li>
              <li>
                <strong>Recupera il codice:</strong>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'grey.100', p: 1, mt: 1, borderRadius: 1 }}>
                  git checkout abc1234 -- .<br />
                  # oppure: git revert [commits problematici]
                </Box>
              </li>
              <li>
                <strong>Bumpa version in package.json:</strong>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'grey.100', p: 1, mt: 1, borderRadius: 1 }}>
                  "version": "1.2.18"  // NON 1.2.16!
                </Box>
              </li>
              <li>
                <strong>Aggiungi nota nelle release notes:</strong>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'grey.100', p: 1, mt: 1, borderRadius: 1 }}>
                  v1.2.18 - Rollback funzionale a v1.2.16<br />
                  Ripristina codebase stabile pre-bug critico
                </Box>
              </li>
              <li>
                <strong>Build & Upload APK</strong> normalmente
              </li>
              <li>
                <strong>Attiva versione</strong> con rollout graduale (10% → 50% → 100%)
              </li>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>⏱️ Timeline Esempio:</Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" component="div">
                • <strong>15:00:</strong> Bug critico rilevato in v1.2.17<br />
                • <strong>15:10:</strong> Team decide rollback a v1.2.16<br />
                • <strong>15:15:</strong> Recuperato codice v1.2.16, creata v1.2.18<br />
                • <strong>15:25:</strong> Build completato, APK caricato<br />
                • <strong>15:30:</strong> Attivata v1.2.18 con rollout 10%<br />
                • <strong>15:30-16:00:</strong> Dispositivi (10%) aggiornano a v1.2.18<br />
                • <strong>16:00:</strong> Verifica ok, rollout aumentato a 100%<br />
                • <strong>17:00:</strong> Tutti i dispositivi tornati a codebase stabile
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Importante:</strong> Questa strategia mantiene versionCode monotono (sempre crescente),
                rispettando le policy di sicurezza Android e garantendo installazione affidabile su tutti i dispositivi.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Obbligatorio */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">🔴 Aggiornamento Obbligatorio</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ '& > *': { mb: 2 } }}>
            <Typography variant="body2">
              Un aggiornamento marcato come <Chip label="OBBLIGATORIO" color="error" size="small" /> 
              {' '}forza l'installazione senza possibilità di posticipare.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Comportamento:</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Il dispositivo mostra l'overlay di update senza pulsante "Later"</li>
              <li>L'installazione parte immediatamente (se in maintenance window)</li>
              <li>Il kiosk rimane bloccato finché l'update non è completo</li>
            </Box>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Usa con cautela:</strong> Gli aggiornamenti obbligatori bloccano completamente i dispositivi.
                Indicato solo per fix critici di sicurezza o bug bloccanti.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Best practice:</strong> Testa sempre con rollout 10% prima di rendere obbligatorio al 100%.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Best Practices */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">✅ Best Practices</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="subtitle2" gutterBottom>Workflow Consigliato per Deploy:</Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
              <li>
                <strong>Crea la nuova versione</strong> con rollout <Chip label="10%" size="small" /> (beta)
              </li>
              <li>
                <strong>Attiva la versione</strong> - ~10% dei dispositivi aggiornerà
              </li>
              <li>
                <strong>Monitora per 1-2 ore</strong> - verifica log e segnalazioni
              </li>
              <li>
                <strong>Aumenta il rollout a 50%</strong> se tutto ok
              </li>
              <li>
                <strong>Monitora per altre 2-4 ore</strong>
              </li>
              <li>
                <strong>Porta al 100%</strong> quando sei sicuro
              </li>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Raccomandazioni Generali:</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Testa sempre in ambiente di staging prima</li>
              <li>Configura maintenance window per orari notturni</li>
              <li>Non usare "Obbligatorio" se non strettamente necessario</li>
              <li>Monitora la sezione "Dispositivi" per vedere adozione real-time</li>
              <li><strong>Per rollback:</strong> usa approccio funzionale (versionCode crescente, codice vecchio)</li>
              <li>Mantieni tag git di ogni release per recupero rapido</li>
              <li>Documenta le release notes dettagliatamente (specialmente per rollback)</li>
            </Box>

            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Pro-tip:</strong> Usa Force Check da Admin WebApp per testare l'update su un dispositivo 
                specifico prima di aumentare il rollout.
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
